import theme from '../../theme';
import { useEffect } from 'react';
import PageHeader from '../../components/PageHeader';
import BackgroundCanvas from '../../components/BackgroundCanvas';
import { LEARN_SUBDOMAIN_URL } from '../../lib/subdomain';

const SHELF_CSS = `
  .shelf-wrapper {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 20px;
  }
  .shelf-framing {
    font-size: 16px;
    color: ${theme.textMuted};
    font-style: italic;
    margin-bottom: 40px;
    line-height: 1.6;
    text-align: center;
  }
  .book-card {
    display: flex;
    background: ${theme.bgCard};
    border: 1px solid rgba(184, 149, 90, 0.25);
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    margin-bottom: 24px;
  }
  .book-card:hover {
    transform: translateY(-4px);
    border-color: rgba(184, 149, 90, 0.5);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.4);
  }
  .book-image-container {
    width: 140px;
    height: 180px;
    background: #070f1c;
    flex-shrink: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid rgba(184, 149, 90, 0.2);
    position: relative;
  }
  .book-author-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.8;
    transition: all 0.3s ease;
    filter: sepia(0.15) contrast(1.05);
  }
  .book-card:hover .book-author-image {
    transform: scale(1.05);
    opacity: 1;
    filter: none;
  }
  .book-content {
    padding: 24px 28px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex-grow: 1;
  }
  .book-header {
    margin-bottom: 8px;
  }
  .book-title {
    font-family: 'EB Garamond', serif;
    font-size: 22px;
    font-weight: 400;
    color: ${theme.gold};
    letter-spacing: 0.01em;
    margin: 0 0 4px 0;
  }
  .book-meta {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.15em;
    color: ${theme.textMuted};
    text-transform: uppercase;
  }
  .book-annotation {
    font-family: 'EB Garamond', serif;
    font-size: 16px;
    color: ${theme.textPrimary};
    line-height: 1.6;
    margin: 12px 0 0 0;
  }
  @media (max-width: 650px) {
    .book-card {
      flex-direction: column;
    }
    .book-image-container {
      width: 100%;
      height: 180px;
      border-right: none;
      border-bottom: 1px solid rgba(184, 149, 90, 0.2);
    }
    .book-content {
      padding: 20px;
    }
  }
`;

const books = [
  {
    title: 'Human Action',
    author: 'Ludwig von Mises',
    year: 1949,
    image: '/images/ludwig_von_mises.png',
    annotation: 'The foundation. Praxeology as the logic of human choice, purposeful action under scarcity. Everything else in Austrian economics follows from this.',
  },
  {
    title: 'Prices and Production',
    author: 'Friedrich Hayek',
    year: 1931,
    image: '/images/friedrich_hayek.png',
    annotation: 'Where the triangle comes from. Hayek traces how credit expansion distorts the structure of production and why the resulting boom must end in bust.',
  },
  {
    title: 'The Pure Theory of Capital',
    author: 'Friedrich Hayek',
    year: 1941,
    image: '/images/friedrich_hayek.png',
    annotation: 'Dense and rewarding. Capital is not a homogeneous fund. It is a structure of heterogeneous goods arranged in time. That distinction matters enormously for investing.',
  },
  {
    title: 'Socialism',
    author: 'Ludwig von Mises',
    year: 1922,
    image: '/images/ludwig_von_mises.png',
    annotation: 'The calculation argument in its original form. Not a political critique, but a demonstration that rational economic calculation is impossible without private ownership of the means of production.',
  },
  {
    title: 'The Use of Knowledge in Society',
    author: 'Friedrich Hayek',
    year: 1945,
    image: '/images/friedrich_hayek.png',
    annotation: 'Nineteen pages. The clearest statement of why dispersed, tacit knowledge cannot be centralised, and why prices are an information system, not just a rationing mechanism.',
  },
  {
    title: "America's Great Depression",
    author: 'Murray Rothbard',
    year: 1963,
    image: '/images/murray_rothbard.png',
    annotation: 'Austrian Business Cycle Theory applied to the 1920s boom. Shows exactly how credit expansion creates the structure of production that then must be liquidated.',
  },
  {
    title: 'Man, Economy, and State',
    author: 'Murray Rothbard',
    year: 1962,
    image: '/images/murray_rothbard.png',
    annotation: 'Rothbard rebuilding economics from first principles. The production structure analysis here is the most thorough working-out of Hayekian capital theory I have found.',
  },
  {
    title: 'The Theory of Money and Credit',
    author: 'Ludwig von Mises',
    year: 1912,
    image: '/images/ludwig_von_mises.png',
    annotation: 'Where Austrian monetary theory originates. The integration of money into the general theory of value, something Walrasian economics still has not managed.',
  },
  {
    title: 'Capital and Interest',
    author: 'Eugen von Böhm-Bawerk',
    year: 1884,
    image: '/images/boehm_bawerk.png',
    annotation: "The origin of time preference as an economic concept. Böhm-Bawerk's insight that roundabout production is more productive but requires patience is the root of everything above.",
  },
  {
    title: 'Economics in One Lesson',
    author: 'Henry Hazlitt',
    year: 1946,
    image: '/images/henry_hazlitt.png',
    annotation: 'The best entry point. Every intervention has visible beneficiaries and invisible costs. Hazlitt makes the unseen visible. Once you see it, you cannot stop.',
  },
];

function BookCard({ title, author, year, image, annotation }) {
  return (
    <div className="book-card">
      <div className="book-image-container">
        <img src={image} alt={author} className="book-author-image" />
      </div>
      <div className="book-content">
        <div className="book-header">
          <h4 className="book-title">{title}</h4>
          <span className="book-meta">{author} · {year}</span>
        </div>
        <p className="book-annotation">{annotation}</p>
      </div>
    </div>
  );
}

function ShelfPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "The Shelf - Austrian Process";
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    const previousCanonical = canonicalLink ? canonicalLink.getAttribute('href') : '';
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://www.austrianprocess.com/shelf');
    }
    return () => {
      if (canonicalLink && previousCanonical) {
        canonicalLink.setAttribute('href', previousCanonical);
      }
    };
  }, []);

  return (
    <>
      <BackgroundCanvas />
      <div 
        className="page-container shelf-wrapper"
        style={{ 
          position: 'relative',
          zIndex: 1,
          background: 'rgba(var(--ap-bg-dark-rgb), 0.45)',
          backdropFilter: 'blur(1px)',
          WebkitBackdropFilter: 'blur(1px)',
          border: '1px solid rgba(184, 149, 90, 0.1)',
          borderRadius: '4px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.45)',
          marginTop: '24px',
          marginBottom: '24px',
          padding: '48px 32px'
        }}
      >
        <style>{SHELF_CSS}</style>

      <PageHeader
        title="The Shelf"
        description="The books behind the framework. Not a bibliography. A record."
      />

      <p className="shelf-framing">
        These are the books that formed the lens. Not everything read on Austrian Economics.
        Just the ones that changed something. The annotations are mine.
      </p>

      {/* The Shelf is a personal record, not a course reading list -- the two
          have different jobs, so the Diploma's assigned reading lives on the
          learn subdomain and is generated from what the lessons cite. This
          links them without turning this page into the bibliography it says
          it isn't. */}
      <p className="shelf-framing" style={{ fontSize: '15px', color: theme.textMuted }}>
        Looking for the Diploma's assigned reading instead?{' '}
        <a
          href={`${LEARN_SUBDOMAIN_URL}/readings`}
          style={{ color: theme.gold, textDecoration: 'underline', textUnderlineOffset: '3px' }}
        >
          That list lives here
        </a>
        {' '}- every text the course sends you to, in the order you meet it.
      </p>

      <div>
        {books.map((book) => (
          <BookCard key={book.title} {...book} />
        ))}
      </div>
    </div>
    </>
  );
}

export default ShelfPage;
